import { Settings, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useReaderSettings } from '../../../hooks/useReaderSettings';
import { controlClasses } from './controls';
import { useTranslation } from '../../../i18n/useTranslation';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  asPanel?: boolean; // If true, render as panel content without modal wrapper
}

export default function SettingsPanel({ isOpen, onClose, asPanel = false }: SettingsPanelProps) {
  const { t } = useTranslation();
  const { settings, updateSettings, isUpdating } = useReaderSettings();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen && !asPanel) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      dialogRef.current?.focus();
    } else if (!isOpen && previousFocusRef.current) {
      previousFocusRef.current.focus?.();
    }
  }, [isOpen, asPanel]);

  // Local state for settings
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [fontSize, setFontSize] = useState(16);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [lineHeight, setLineHeight] = useState(1.6);
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [textColor, setTextColor] = useState('#000000');
  const [autoHideToolbar, setAutoHideToolbar] = useState(false);
  const [autoHideDelay, setAutoHideDelay] = useState(3000);
  const [useBookStyles, setUseBookStyles] = useState(true); // Default: use book styles

  // Sync with backend settings
  useEffect(() => {
    if (settings) {
      setTheme(settings.theme);
      setFontSize(settings.fontSize);
      setFontFamily(settings.fontFamily);
      setLineHeight(settings.lineHeight);
      setBackgroundColor(settings.backgroundColor);
      setTextColor(settings.textColor);
      setAutoHideToolbar(settings.autoHideToolbar);
      setAutoHideDelay(settings.autoHideDelay);
      setUseBookStyles(settings.useBookStyles);
    }
  }, [settings]);

  const handleSave = async () => {
    await updateSettings({
      theme,
      fontSize,
      fontFamily,
      lineHeight,
      backgroundColor,
      textColor,
      autoHideToolbar,
      autoHideDelay,
      useBookStyles,
    });
    onClose();
  };

  const handleReset = () => {
    setTheme('light');
    setFontSize(16);
    setFontFamily('system-ui');
    setLineHeight(1.6);
    setBackgroundColor('#FFFFFF');
    setTextColor('#000000');
    setAutoHideToolbar(false);
    setAutoHideDelay(3000);
    setUseBookStyles(true); // Default: use book styles
  };

  if (!isOpen) return null;

  // Settings form content
  const settingsContent = (
    <>
      {/* Settings Form */}
      <div className="px-4 py-3 space-y-3">
          {/* Use Custom Styles Toggle */}
          <div className="pb-3 border-b border-neutral-200">
            <label className="flex items-start gap-2" htmlFor="use-custom-styles-checkbox">
              <input
                id="use-custom-styles-checkbox"
                type="checkbox"
                checked={!useBookStyles}
                onChange={(e) => setUseBookStyles(!e.target.checked)}
                className="w-4 h-4 mt-0.5 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
              />
              <div>
                <span className="text-xs font-semibold text-neutral-800">
                  {t('App.Reader.Settings.UseCustomStyles')}
                </span>
                <p className="text-[11px] text-neutral-500 mt-0.5 leading-snug">
                  {t('App.Reader.Settings.UseCustomStylesHint')}
                </p>
              </div>
            </label>
          </div>

          {/* Customization Options - Only shown when useCustomStyles is enabled */}
          {!useBookStyles && (
          <>
          {/* Theme */}
          <div>
            <label className={controlClasses.fieldLabel}>
              {t('App.Reader.Settings.Theme')}
            </label>
            <div className="flex gap-2" role="tablist" aria-label={t('App.Reader.Settings.Theme')}>
              <button
                onClick={() => setTheme('light')}
                role="tab"
                aria-selected={theme === 'light'}
                tabIndex={theme === 'light' ? 0 : -1}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md border transition-all ${
                  theme === 'light'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
                }`}
              >
                {t('App.Reader.Settings.ThemeLight')}
              </button>
              <button
                onClick={() => setTheme('dark')}
                role="tab"
                aria-selected={theme === 'dark'}
                tabIndex={theme === 'dark' ? 0 : -1}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md border transition-all ${
                  theme === 'dark'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
                }`}
              >
                {t('App.Reader.Settings.ThemeDark')}
              </button>
            </div>
          </div>

          {/* Font Family */}
          <div>
            <label className={controlClasses.fieldLabel}>
              {t('App.Reader.Settings.FontFamily')}
            </label>
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className={controlClasses.input}
            >
              <option value="system-ui">{t('App.Reader.Settings.FontDefault')}</option>

              <optgroup label={t('App.Reader.Settings.FontGroupSansSerif')}>
                <option value="Open Sans">Open Sans</option>
                <option value="Arial">Arial</option>
                <option value="Verdana">Verdana</option>
                <option value="Helvetica">Helvetica</option>
              </optgroup>

              <optgroup label={t('App.Reader.Settings.FontGroupSerif')}>
                <option value="Noto Serif">Noto Serif</option>
                <option value="Old Standard TT">Old Standard TT</option>
                <option value="Bitter">Bitter</option>
                <option value="Alegreya">Alegreya</option>
                <option value="Georgia">Georgia</option>
                <option value="Times New Roman">Times New Roman</option>
              </optgroup>

              <optgroup label={t('App.Reader.Settings.FontGroupMonospace')}>
                <option value="Roboto Mono">Roboto Mono</option>
                <option value="Courier New">Courier New</option>
              </optgroup>

              <optgroup label={t('App.Reader.Settings.FontGroupAccessibility')}>
                <option value="OpenDyslexic">OpenDyslexic (Dyslexia-friendly)</option>
              </optgroup>
            </select>
          </div>

          {/* Font Size */}
          <div>
            <label htmlFor="font-size-range" className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
              <span>{t('App.Reader.Settings.FontSize')}</span>
              <span className="text-neutral-700 normal-case">{fontSize}px</span>
            </label>
            <input
              id="font-size-range"
              type="range"
              min="12"
              max="24"
              step="1"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="w-full h-1.5 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              aria-label="Font size"
              aria-valuenow={fontSize}
              aria-valuemin={12}
              aria-valuemax={24}
              aria-valuetext={`${fontSize}px`}
            />
            <div className="flex justify-between text-[10px] text-neutral-400 mt-0.5">
              <span>12</span>
              <span>18</span>
              <span>24</span>
            </div>
          </div>

          {/* Line Height */}
          <div>
            <label htmlFor="line-height-range" className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
              <span>{t('App.Reader.Settings.LineHeight')}</span>
              <span className="text-neutral-700 normal-case">{lineHeight}</span>
            </label>
            <input
              id="line-height-range"
              type="range"
              min="1.2"
              max="2.0"
              step="0.1"
              value={lineHeight}
              onChange={(e) => setLineHeight(Number(e.target.value))}
              className="w-full h-1.5 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
              aria-label="Line height"
              aria-valuenow={lineHeight}
              aria-valuemin={1.2}
              aria-valuemax={2.0}
              aria-valuetext={`${lineHeight}`}
            />
            <div className="flex justify-between text-[10px] text-neutral-400 mt-0.5">
              <span>{t('App.Reader.Settings.LineHeightCompact')}</span>
              <span>{t('App.Reader.Settings.LineHeightNormal')}</span>
              <span>{t('App.Reader.Settings.LineHeightLoose')}</span>
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="bg-color-picker" className="block text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
                {t('App.Reader.Settings.ColorBackground')}
              </label>
              <div className="flex gap-1.5 items-center">
                <input
                  id="bg-color-picker"
                  type="color"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className="w-7 h-7 rounded border border-neutral-300 cursor-pointer flex-shrink-0"
                />
                <input
                  type="text"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  className={`${controlClasses.input} flex-1 min-w-0`}
                  placeholder="#FFFFFF"
                />
              </div>
            </div>

            <div>
              <label htmlFor="text-color-picker" className="block text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1">
                {t('App.Reader.Settings.ColorText')}
              </label>
              <div className="flex gap-1.5 items-center">
                <input
                  id="text-color-picker"
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="w-7 h-7 rounded border border-neutral-300 cursor-pointer flex-shrink-0"
                />
                <input
                  type="text"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className={`${controlClasses.input} flex-1 min-w-0`}
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>

          {/* Auto-hide Toolbar */}
          <div>
            <label className="flex items-center gap-2" htmlFor="auto-hide-toolbar-checkbox">
              <input
                id="auto-hide-toolbar-checkbox"
                type="checkbox"
                checked={autoHideToolbar}
                onChange={(e) => setAutoHideToolbar(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
              />
              <span className="text-xs font-medium text-neutral-700">
                {t('App.Reader.Settings.AutoHideToolbar')}
              </span>
            </label>
            {autoHideToolbar && (
              <div className="mt-2 ml-6">
                <label htmlFor="auto-hide-delay-range" className="flex items-center justify-between text-[11px] text-neutral-500 mb-1">
                  <span>{t('App.Reader.Settings.AutoHideDelay')}</span>
                  <span className="text-neutral-700">{autoHideDelay / 1000}s</span>
                </label>
                <input
                  id="auto-hide-delay-range"
                  type="range"
                  min="1000"
                  max="10000"
                  step="1000"
                  value={autoHideDelay}
                  onChange={(e) => setAutoHideDelay(Number(e.target.value))}
                  className="w-full h-1.5 bg-neutral-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  aria-label="Auto-hide delay"
                  aria-valuenow={autoHideDelay}
                  aria-valuemin={1000}
                  aria-valuemax={10000}
                  aria-valuetext={`${autoHideDelay / 1000} seconds`}
                />
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="border border-neutral-200 rounded-md p-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">{t('App.Reader.Settings.Preview')}</h3>
            <div
              className="p-2 rounded text-sm"
              style={{
                backgroundColor,
                color: textColor,
                fontFamily,
                fontSize: `${fontSize}px`,
                lineHeight: `${lineHeight}`,
              }}
            >
              <p>
                {t('App.Reader.Settings.PreviewText')}
              </p>
            </div>
          </div>
          </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-neutral-50 border-t border-neutral-200 px-4 py-3 flex justify-between items-center gap-2">
          <button
            onClick={handleReset}
            className="px-2 py-1.5 text-[13px] font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-md transition-colors whitespace-nowrap"
            title={t('App.Reader.Settings.ResetTooltip')}
          >
            {t('App.Reader.Settings.Reset')}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className={controlClasses.btnSecondary}>
              {t('App.Reader.Settings.Cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isUpdating}
              className={controlClasses.btnPrimary}
            >
              {isUpdating ? t('App.Reader.Settings.Saving') : t('App.Reader.Settings.Save')}
            </button>
          </div>
        </div>
    </>
  );

  // If rendering as a panel, return content directly
  if (asPanel) {
    return settingsContent;
  }

  // Otherwise, wrap in modal
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[1100] flex items-center justify-center p-4"
      role="presentation"
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-panel-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary-600" aria-hidden="true" />
            <h2 id="settings-panel-title" className="text-xl font-semibold text-neutral-800">{t('App.Reader.Settings.Title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
            aria-label={t('App.Reader.Settings.CloseAriaLabel')}
          >
            <X className="w-5 h-5 text-neutral-500" aria-hidden="true" />
          </button>
        </div>
        {settingsContent}
      </div>
    </div>
  );
}
